import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "react-query";
import "./index.css";
import "./styles/white-label-restricar.css";
import "./styles/theme-palettes.css";
import AppRouter from "./routes/AppRouter";
import { applyWhiteLabelTheme } from "./utils/whiteLabel";
import {
  applyStoredThemePalette,
  syncThemePaletteFromHost,
} from "./utils/themePalette";
import { PermissionProvider } from "./contexts/ApplicationContext";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";

// Antes do render para o tema já valer na primeira pintura.
applyWhiteLabelTheme();

/* Última paleta conhecida deste host, para não piscar azul antes de a cor real
   chegar. Quem tem tema por domínio em CSS não é afetado. */
applyStoredThemePalette();

/* E, em paralelo ao render, confirma a cor com o servidor a partir do domínio.
   É o que pinta a TELA DE LOGIN de um representante com domínio próprio — ali
   ainda não existe sessão, então o domínio é a única pista de quem é o dono.
   Não bloqueia o boot: a página sobe com a cor lembrada e se ajusta quando a
   resposta chega (na primeira visita àquele domínio, é a única fonte). */
syncThemePaletteFromHost();

/* Tira a tela de carregamento do index.html, que cobre tudo com z-index 100000.
   Idempotente: é chamada por três caminhos (montagem do React, `load` e o
   tempo-limite abaixo) e vale a primeira que chegar. */
const removeLoadingSpinner = () => {
  const loadingContainer = document.getElementById("loading-container");
  if (loadingContainer) {
    loadingContainer.style.display = "none";
  }
};

/* Quem tira o overlay é a MONTAGEM do React — o efeito roda no primeiro commit,
   ou seja, quando o painel já está pintado atrás dele.

   Antes isso dependia só do evento `load`, que espera TODOS os subrecursos da
   página, inclusive os de terceiros (CDN, fontes, recaptcha, OneSignal). Numa
   rede corporativa que bloqueia um desses hosts engolindo o pacote — em vez de
   recusar a conexão — o `load` nunca dispara: o cliente ficava com a tela de
   "carregando" para sempre, mesmo com o sistema inteiro já funcionando atrás
   do overlay. Era exatamente o que os clientes relatavam. */
const EsconderCarregamento = () => {
  useEffect(removeLoadingSpinner, []);
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      staleTime: Infinity,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
/* NÃO coloque um provider do Google Maps aqui. O <LoadScript> que ficava neste
   ponto só renderizava os filhos depois que maps.googleapis.com respondia: numa
   rede que bloqueia aquele host engolindo o pacote, o app nunca montava e o
   cliente ficava na tela de carregamento, mesmo sem abrir nenhuma tela de mapa.
   Quem precisa de mapa carrega sob demanda — ver utils/googleMaps.js. */
root.render(
  <QueryClientProvider client={queryClient}>
    <PermissionProvider>
      <EsconderCarregamento />
      <AppRouter />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
    </PermissionProvider>
  </QueryClientProvider>,
);

/* Redes de segurança, nesta ordem de chegada:
   - `load`: caminho antigo, mantido para o caso de o efeito não rodar;
   - tempo-limite: se o React não montar (erro fatal no bundle), é melhor
     mostrar a página como ela está do que deixar o cliente num "carregando"
     eterno, que não dá nenhuma pista do que houve. */
window.addEventListener("load", removeLoadingSpinner);
setTimeout(removeLoadingSpinner, 20000);
